import { USER_ACCESS_LEVEL } from './constants';
import { isOwner } from './productAccess';

/**
 * Per-user, per-workspace capabilities.
 *
 * Distinct from PAC (`productAccess.js`), which resolves per insider profile and
 * per org. A capability is granted to one PERSON in one workspace and arrives on
 * the session as `workspace_capabilities`, mirrored from the matching
 * `workspace_memberships` entry on every login and workspace switch.
 *
 * ── Why this matters for gating ──────────────────────────────────────────────
 * "LC Teacher" is **not a role**. A plain classroom teacher and an LC Teacher are
 * both `access_level: 23` with `workspace_role: 'teacher'`; the only difference
 * is whether the membership carries `create_learning_program`.
 *
 * So gate authoring on the CAPABILITY, never on the level or the role. The same
 * capability can later be granted to a peer or a school lead, and every check
 * written this way keeps working with no edit.
 *
 * As always in this file's neighbourhood: these gates are UX only. The server
 * enforces the same rule and returns `403 CAPABILITY_REQUIRED`.
 */

export const MEMBERSHIP_CAPABILITY = {
    CREATE_LEARNING_PROGRAM: 'create_learning_program',
};

/** True for any teacher account, LC or not. */
export const isTeacher = (user) =>
    Number(user?.access_level) === USER_ACCESS_LEVEL.TEACHER_ACCESS_DQ_INSIDER_PORTAL;

/** True when this user holds `capability` in the workspace they are acting in. */
export const hasCapability = (user, capability) =>
    Array.isArray(user?.workspace_capabilities)
    && user.workspace_capabilities.includes(capability);

/**
 * A teacher who may author learning programs — what the product calls an
 * "LC Teacher". Use this for the authoring and distribution surfaces.
 */
export const isLcTeacher = (user) =>
    isTeacher(user) && hasCapability(user, MEMBERSHIP_CAPABILITY.CREATE_LEARNING_PROGRAM);

/**
 * May this user AUTHOR learning programs, capability-wise?
 *
 * The server guards authoring twice and both gates must pass: the ORG needs the
 * `create` permission on the learning_program product (`canCreateProduct`), and
 * the USER needs `create_learning_program` — or must be a workspace owner, whom
 * capabilityGuard lets through implicitly. That owner branch is what a school
 * NETWORK relies on: its contact sits at level 21 and holds no capabilities.
 *
 * Pair it with `canCreateProduct`; on its own it answers only half the question.
 * Checking only the product half is what let a school lead into the builder and
 * then collect a 403 on submit.
 */
export const canAuthorLearningProgram = (user) =>
    isOwner(user) || hasCapability(user, MEMBERSHIP_CAPABILITY.CREATE_LEARNING_PROGRAM);

/**
 * Classes this teacher is bound to, from the membership for the active
 * workspace.
 *
 * An empty array means "no classes assigned yet" — **not** "all classes". Never
 * fall back to showing everything when this is empty.
 */
export const membershipSectionCodes = (user) => {
    const memberships = Array.isArray(user?.workspace_memberships) ? user.workspace_memberships : [];
    const active = memberships.find(
        (m) => m && m.is_active !== false && String(m.dq_insider_id) === String(user?.dq_insider_id),
    );
    return Array.isArray(active?.section_codes) ? active.section_codes : [];
};

/** True when a teacher may act on this class. Non-teachers are unconstrained here. */
export const canActOnSection = (user, sectionCode) => {
    if (!user) return false;
    if (!isTeacher(user)) return true;
    if (!sectionCode) return false;
    return membershipSectionCodes(user).includes(String(sectionCode));
};

/**
 * A CLIENT workspace — a school, or a school network.
 *
 * The two sit at different access levels: a school network's contact is 21
 * (FULL_ACCESS_..._SINGLE_INSTITUTE) because a network is the level above the
 * schools it operates, and a school's contact is 22 (PARTIAL_...) whether that
 * school stands alone or sits inside a network.
 *
 * DELIBERATELY NARROW. Only two places use this — the sidebar (Main.comp.jsx)
 * and the route guard (PrivateRouteWithLayout.js), i.e. exactly what a network
 * needs to reach its own menu. Every other `access_level === 22` check in the
 * client was left alone on purpose, so this change disturbs nothing that a
 * school, teacher or partner already does.
 *
 * The consequence, recorded rather than hidden: on pages that still compare to
 * 22 by hand, a network at 21 is treated as the partner. AccountSettings is the
 * one that matters — its `isSchool` check withholds the billing tabs, so a
 * network currently sees them. Raise it before a network account goes live.
 *
 * WHY THE FLAG IS PART OF THE TEST. Level 21 predates this work and is held by
 * accounts that are not school networks; matching on 21 alone would narrow what
 * those accounts can already reach. With the flag, this predicate is a strict
 * superset of the old `=== 22` check: everyone it used to be true for it is
 * still true for, plus networks.
 *
 * A DIRECT WORKSPACE (23 Sep 2026) — a school or school network that bought a direct
 * plan for itself — is opened the same way: its owner sits at 21 with its own profile,
 * and the session's `is_direct_workspace` makes it a School Workspace or, with
 * `is_school_network`, a Network Workspace. It keeps its owner rights (buying, and
 * Billing and Payments in Settings, which the `isSchool` check leaves to level 21).
 *
 * As elsewhere in this file: UX only. The server enforces the same boundaries.
 */
export const isClientWorkspace = (user) => {
    const level = Number(user?.access_level);
    return level === USER_ACCESS_LEVEL.PARTIAL_ACCESS_DQ_INSIDER_PORTAL_SINGLE_INSTITUTE
        || (level === USER_ACCESS_LEVEL.FULL_ACCESS_DQ_INSIDER_PORTAL_SINGLE_INSTITUTE
            && (user?.is_school_network === true || user?.is_direct_workspace === true));
};

/** A school or school network on a direct plan, bought for itself (23 Sep 2026). */
export const isDirectWorkspace = (user) => user?.is_direct_workspace === true;
